import { Add } from "@mui/icons-material";
import { Button, Chip, Stack, Tooltip, Typography } from "@mui/material";
import { useState } from "react";
import { useMedicalCareContext } from "../../../../contexts/MedicalCareContext";
import { useWatch } from "react-hook-form";
import InputText from "@/components/form/InputText";
import { toast } from "react-toastify";

interface AddCondutaProps {
  /**
   * Condutas em aberto já calculadas em `Condutas.tsx`. Comparar sobre os dados
   * evita o falso negativo do texto truncado na tela e o falso positivo de ler
   * qualquer Typography descendente (data, especialidade, profissional).
   */
  openItens?: string[];
}

/** Normalização única usada na comparação de duplicatas. */
const normalize = (value?: string) => value?.trim()?.toLowerCase() || "";

export function AddConduta({ openItens = [] }: AddCondutaProps) {
  const { control, unregister, condutas, setCondutas } =
    useMedicalCareContext();
  const [currentIndex, setCurrentIndex] = useState<number>(
    condutas?.length || 0
  );

  const formValues = useWatch({
    name: "New_Conduta",
    control,
  }) as string[];

  // Duplicata é verificada apenas na tentativa de adicionar (Enter ou botão):
  // durante a digitação, qualquer prefixo coincidente dava match e o texto do
  // profissional era apagado.
  const handleAddConduta = () => {
    const currentValue = formValues?.[currentIndex];
    if (currentValue?.trim()) {
      const currentValueKey = normalize(currentValue);

      const existsInCurrentList = condutas?.some(
        (conduta: string) => normalize(conduta) === currentValueKey
      );

      const existsInOpenList = openItens?.some(
        (conduta: string) => normalize(conduta) === currentValueKey
      );

      if (existsInCurrentList || existsInOpenList) {
        // Bloqueia a inclusão e PRESERVA o que foi digitado.
        toast.warning(`"${currentValue}" já está na lista de condutas`, {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });

        return;
      }

      setCondutas((prev: string[]) => [...prev, currentValue]);
      setCurrentIndex((prev: number) => prev + 1);
    }
  };

  return (
    <Stack spacing={2}>
      {condutas?.length > 0 && (
        <Stack
          direction="row"
          flexWrap="wrap"
          spacing={1}
          paddingBottom={1}
          gap={1}
        >
          {condutas.map(
            (conduta: string, index: number) =>
              conduta?.length > 0 && (
                <Chip
                  key={index}
                  sx={{ padding: "8px", height: "auto" }}
                  label={
                    conduta.length > 85 ? (
                      <Tooltip
                        placement="right"
                        title={conduta}
                        slotProps={{
                          popper: {
                            modifiers: [
                              {
                                name: "offset",
                                options: { offset: [80, 20] },
                              },
                            ],
                          },
                        }}
                      >
                        <Typography>{conduta.substring(0, 85)}...</Typography>
                      </Tooltip>
                    ) : (
                      <Typography>{conduta}</Typography>
                    )
                  }
                  onDelete={() => {
                    setCondutas((prev: string[]) => {
                      const updated = [...prev];
                      updated[index] = "";
                      return updated;
                    });
                    unregister(`New_Conduta.${index}`);
                  }}
                />
              )
          )}
        </Stack>
      )}

      <Stack spacing={1}>
        <InputText
          key={`conduta-input-${currentIndex}`}
          label="Conduta"
          name={`New_Conduta.${currentIndex}`}
          control={control}
          sx={{ mb: 4 }}
          onKeyDown={(e: React.KeyboardEvent) =>
            e.key === "Enter" && (e.preventDefault(), handleAddConduta())
          }
          minRows={3}
          maxRows={100}
          autoFocus
          multiline
        />

        <Stack direction="row" justifyContent="flex-end">
          <Button
            sx={{ width: "fit-content" }}
            onClick={handleAddConduta}
            variant="text"
          >
            <Add />
            Adicionar
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
}
